import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

export default function OrderDetails() {
  const API_URL = process.env.REACT_APP_DRIVER_API_URL || process.env.REACT_APP_API_URL || "http://localhost:5566/";
  const BASE = API_URL.endsWith("/") ? API_URL + "shypvdriverapp" : API_URL + "/shypvdriverapp";

  // Tabs
  const [activeTab, setActiveTab] = useState("dashboard");

  // Orders list
  const [orders, setOrders] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Quick search
  const [quickSearch, setQuickSearch] = useState("");

  // Filters
  const [filterJobId, setFilterJobId] = useState("");
  const [filterAwb, setFilterAwb] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDriverId, setFilterDriverId] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // Detail modal
  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailTab, setDetailTab] = useState("info");

  // POD upload audit (every S3 upload attempt for this AWB, success and failure)
  const [podAuditLogs, setPodAuditLogs] = useState([]);
  const podAuditCount = podAuditLogs.length;

  // Image viewer
  const [viewingImage, setViewingImage] = useState(null);

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Sub-shipment (child AWB) tick-off selection
  const [selectedChildren, setSelectedChildren] = useState(() => new Set());
  const [updatingChildren, setUpdatingChildren] = useState(false);

  // Stats
  const [stats, setStats] = useState({ total: 0, delivered: 0, failed: 0, pending: 0, assigned: 0, pickedUp: 0 });

  useEffect(() => {
    fetchRecentOrders();
  }, []);

  useEffect(() => {
    if (activeTab === "orders") fetchRecentOrders();
  }, [page, pageSize]);

  // ============ API CALLS ============

  const fetchRecentOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const token = sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${BASE}/api/tms/order-details/recent`, {
        params: { page, size: pageSize, type: "DROP" },
        headers,
      });
      if (res.data.success) {
        const data = res.data.data || [];
        setOrders(data);
        setTotalElements(res.data.totalElements || 0);
        setTotalPages(res.data.totalPages || 0);
        computeStats(data);
      }
    } catch (err) {
      handleApiError(err, "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const searchOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const token = sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = { page, size: pageSize };
      if (filterJobId) params.jobId = filterJobId;
      if (filterAwb) params.awbNumber = filterAwb;
      if (filterStatus) params.status = filterStatus;
      if (filterDriverId) params.driverId = filterDriverId;
      if (filterDateFrom) params.dateFrom = filterDateFrom;
      if (filterDateTo) params.dateTo = filterDateTo;
      const res = await axios.get(`${BASE}/api/tms/order-details/search`, { params, headers });
      if (res.data.success) {
        const data = res.data.data || [];
        setOrders(data);
        setTotalElements(res.data.totalElements || 0);
        setTotalPages(res.data.totalPages || 0);
        computeStats(data);
      }
    } catch (err) {
      handleApiError(err, "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetail = async (jobId) => {
    setLoading(true);
    setError("");
    setPodAuditLogs([]);
    try {
      const token = sessionStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${BASE}/api/tms/order-details/${encodeURIComponent(jobId)}`, { headers });
      if (res.data.success) {
        setDetail(res.data.data);
        setDetailTab("info");
        setShowDetail(true);
        // Fetch the standalone POD upload audit for this AWB (every attempt, success + failure).
        // Lives in the V2 service; non-fatal if it can't load.
        const awbForAudit = res.data.data?.order?.businessRefNo || res.data.data?.order?.jobId || jobId;
        try {
          const auditRes = await axios.get(
            `${BASE}/api/emirates-scan-v2/s3-uploads/by-awb/${encodeURIComponent(awbForAudit)}`,
            { headers });
          setPodAuditLogs(auditRes.data?.data || []);
        } catch (_) { /* audit panel is optional */ }
      } else {
        setError(res.data.message || "Order not found");
      }
    } catch (err) {
      handleApiError(err, "Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSearch = useCallback(() => {
    const q = quickSearch.trim();
    if (!q) { fetchRecentOrders(); return; }
    setFilterJobId(q);
    setFilterAwb("");
    setFilterStatus("");
    setFilterDriverId("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setPage(0);
    setActiveTab("orders");
    // Try direct lookup first
    fetchOrderDetail(q);
  }, [quickSearch]);

  const clearFilters = () => {
    setFilterJobId("");
    setFilterAwb("");
    setFilterStatus("");
    setFilterDriverId("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setQuickSearch("");
    setPage(0);
    fetchRecentOrders();
  };

  // Driver tick-off: mark selected sub-shipments PICKED_UP / DELIVERED, then refresh.
  const updateChildAwbStatus = async (newStatus) => {
    if (selectedChildren.size === 0) { setError("Select at least one sub-shipment"); return; }
    setUpdatingChildren(true); setError(""); setSuccess("");
    try {
      const token = sessionStorage.getItem("token");
      const headers = token
        ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" };
      const updates = Array.from(selectedChildren).map((awb) => ({ childAwb: awb, status: newStatus }));
      const res = await axios.post(
        `${BASE}/jobs/updateChildAwbStatus`,
        { jobId: detail.order.jobId, updates },
        { headers });
      if (res.data && res.data.success) {
        setSuccess(`Updated ${selectedChildren.size} sub-shipment(s) to ${newStatus}`);
        setSelectedChildren(new Set());
        await fetchOrderDetail(detail.order.jobId);
      } else {
        setError((res.data && res.data.message) || "Update failed");
      }
    } catch (err) {
      handleApiError(err, "Failed to update sub-shipments");
    } finally {
      setUpdatingChildren(false);
    }
  };

  const handleApiError = (err, fallbackMsg) => {
    if (err.response?.status === 401) {
      setError("Session expired. Please log in again.");
    } else if (err.response?.status === 404) {
      setError("Order not found. Please check the Job ID or AWB number.");
    } else if (err.response?.status >= 500) {
      setError("Server error. Please try again later.");
    } else if (!err.response) {
      setError("Network error. Please check your connection.");
    } else {
      setError(err.response?.data?.message || fallbackMsg);
    }
  };

  const computeStats = (data) => {
    const s = { total: data.length, delivered: 0, failed: 0, pending: 0, assigned: 0, pickedUp: 0 };
    data.forEach((o) => {
      const st = (o.status || "").toUpperCase();
      if (st === "DELIVERED") s.delivered++;
      else if (st === "FAILED" || st === "CANCELLED") s.failed++;
      else if (st === "PENDING" || st === "READY" || st === "UNASSIGNED" || st === "PLANNED") s.pending++;
      else if (st === "ASSIGNED") s.assigned++;
      else if (st === "PICKED_UP" || st === "DROP_ENROUTE" || st === "PICKUP_ENROUTE") s.pickedUp++;
    });
    setStats(s);
  };

  // ============ HELPERS ============

  const statusBadge = (status) => {
    const colors = {
      DELIVERED: { bg: "#dcfce7", color: "#166534" },
      PICKED_UP: { bg: "#dbeafe", color: "#1e40af" },
      DROP_ENROUTE: { bg: "#dbeafe", color: "#1e40af" },
      PICKUP_ENROUTE: { bg: "#e0e7ff", color: "#3730a3" },
      ASSIGNED: { bg: "#fef3c7", color: "#92400e" },
      FAILED: { bg: "#fee2e2", color: "#991b1b" },
      CANCELLED: { bg: "#fee2e2", color: "#991b1b" },
      READY: { bg: "#f3f4f6", color: "#374151" },
      PENDING: { bg: "#fef3c7", color: "#92400e" },
      PLANNED: { bg: "#e0e7ff", color: "#3730a3" },
      UNASSIGNED: { bg: "#f3f4f6", color: "#374151" },
      SENT: { bg: "#dcfce7", color: "#166534" },
      INSCAN: { bg: "#dbeafe", color: "#1e40af" },
    };
    const c = colors[(status || "").toUpperCase()] || { bg: "#f3f4f6", color: "#374151" };
    return (
      <span style={{ ...st.badge, backgroundColor: c.bg, color: c.color }}>{status || "N/A"}</span>
    );
  };

  const formatDate = (dt) => {
    if (!dt) return "-";
    try {
      return new Date(dt).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return dt;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess("Copied to clipboard!");
      setTimeout(() => setSuccess(""), 2000);
    });
  };

  // ============ RENDER ============

  return (
    <div style={st.container}>
      {/* Header */}
      <div style={st.header}>
        <div>
          <h1 style={st.title}>
            <i className="fa fa-list-alt" style={{ marginRight: "10px", color: "#4f7df3" }} />
            Order Details Monitor
          </h1>
          <p style={st.subtitle}>Search, view and track order details, status history, and delivery proof</p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button style={st.btnRefresh} onClick={() => { fetchRecentOrders(); setSuccess("Refreshed"); setTimeout(() => setSuccess(""), 2000); }}>
            <i className="fa fa-refresh" /> Refresh
          </button>
        </div>
      </div>

      {/* Quick Search Bar */}
      <div style={st.quickSearchWrap}>
        <div style={st.quickSearchInner}>
          <i className="fa fa-search" style={{ color: "#9ca3af", fontSize: "16px" }} />
          <input
            style={st.quickSearchInput}
            placeholder="Quick search — enter Job ID or AWB number and press Enter..."
            value={quickSearch}
            onChange={(e) => setQuickSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleQuickSearch(); }}
          />
          {quickSearch && (
            <button style={st.quickSearchClear} onClick={() => { setQuickSearch(""); fetchRecentOrders(); }}>
              <i className="fa fa-times" />
            </button>
          )}
          <button style={st.quickSearchBtn} onClick={handleQuickSearch}>
            Search
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={st.alertError}>
          <span><i className="fa fa-exclamation-circle" style={{ marginRight: "8px" }} />{error}</span>
          <span style={st.closeBtn} onClick={() => setError("")}>&times;</span>
        </div>
      )}
      {success && (
        <div style={st.alertSuccess}>
          <span><i className="fa fa-check-circle" style={{ marginRight: "8px" }} />{success}</span>
          <span style={st.closeBtn} onClick={() => setSuccess("")}>&times;</span>
        </div>
      )}

      {/* Tabs */}
      <div style={st.tabs}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "fa-tachometer" },
          { key: "orders", label: "Order List", icon: "fa-table" },
          { key: "search", label: "Advanced Search", icon: "fa-filter" },
        ].map((tab) => (
          <button
            key={tab.key}
            style={{ ...st.tab, ...(activeTab === tab.key ? st.tabActive : st.tabInactive) }}
            onClick={() => { setActiveTab(tab.key); if (tab.key === "orders") { setPage(0); fetchRecentOrders(); } }}
          >
            <i className={`fa ${tab.icon}`} style={{ marginRight: "6px" }} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ============ DASHBOARD TAB ============ */}
      {activeTab === "dashboard" && (
        <div>
          {loading && <div style={st.spinner}><div style={st.spinnerInner} /></div>}

          {/* Stats Cards */}
          <div style={st.statsGrid}>
            <div style={{ ...st.statCard, borderLeft: "4px solid #4f7df3" }}>
              <div style={st.statIcon}><i className="fa fa-list-alt" style={{ color: "#4f7df3" }} /></div>
              <div><div style={st.statNumber}>{stats.total}</div><div style={st.statLabel}>Total Orders</div></div>
            </div>
            <div style={{ ...st.statCard, borderLeft: "4px solid #10b981" }}>
              <div style={st.statIcon}><i className="fa fa-check-circle" style={{ color: "#10b981" }} /></div>
              <div><div style={st.statNumber}>{stats.delivered}</div><div style={st.statLabel}>Delivered</div></div>
            </div>
            <div style={{ ...st.statCard, borderLeft: "4px solid #ef4444" }}>
              <div style={st.statIcon}><i className="fa fa-times-circle" style={{ color: "#ef4444" }} /></div>
              <div><div style={st.statNumber}>{stats.failed}</div><div style={st.statLabel}>Failed / Cancelled</div></div>
            </div>
            <div style={{ ...st.statCard, borderLeft: "4px solid #f59e0b" }}>
              <div style={st.statIcon}><i className="fa fa-clock-o" style={{ color: "#f59e0b" }} /></div>
              <div><div style={st.statNumber}>{stats.pending}</div><div style={st.statLabel}>Pending / Ready</div></div>
            </div>
            <div style={{ ...st.statCard, borderLeft: "4px solid #8b5cf6" }}>
              <div style={st.statIcon}><i className="fa fa-user-plus" style={{ color: "#8b5cf6" }} /></div>
              <div><div style={st.statNumber}>{stats.assigned}</div><div style={st.statLabel}>Assigned</div></div>
            </div>
            <div style={{ ...st.statCard, borderLeft: "4px solid #06b6d4" }}>
              <div style={st.statIcon}><i className="fa fa-truck" style={{ color: "#06b6d4" }} /></div>
              <div><div style={st.statNumber}>{stats.pickedUp}</div><div style={st.statLabel}>In Transit</div></div>
            </div>
          </div>

          {/* Recent Orders Quick View */}
          <div style={st.card}>
            <div style={st.cardHeader}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={st.cardTitle}><i className="fa fa-clock-o" style={{ marginRight: "8px" }} />Recent Orders</h3>
                <button style={st.btnSmall} onClick={() => setActiveTab("orders")}>View All <i className="fa fa-arrow-right" style={{ marginLeft: "4px" }} /></button>
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              {orders.length === 0 ? (
                <div style={st.emptyState}>
                  <i className="fa fa-inbox" style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "12px" }} />
                  <div style={{ fontSize: "15px", color: "#6b7280" }}>No orders found</div>
                  <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "4px" }}>Try searching with a Job ID or AWB number above</div>
                </div>
              ) : (
                <table style={st.table}>
                  <thead>
                    <tr style={st.tableHead}>
                      <th style={st.th}>Job ID</th>
                      <th style={st.th}>Status</th>
                      <th style={st.th}>Type</th>
                      <th style={st.th}>Receiver</th>
                      <th style={st.th}>Driver</th>
                      <th style={st.th}>Payment</th>
                      <th style={st.th}>Date</th>
                      <th style={st.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 10).map((order, idx) => (
                      <tr key={idx} style={st.tr}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f0f7ff"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                          onClick={() => fetchOrderDetail(order.jobId)}>
                        <td style={st.td}><span style={st.mono}>{order.jobId}</span></td>
                        <td style={st.td}>{statusBadge(order.status)}</td>
                        <td style={st.td}>{order.type || "-"}</td>
                        <td style={st.td}>
                          <div style={{ fontSize: "13px", fontWeight: 500 }}>{order.receiverName || order.contactName || "-"}</div>
                          {order.contactNumber && <div style={{ fontSize: "11px", color: "#9ca3af" }}>{order.contactNumber}</div>}
                        </td>
                        <td style={st.td}>{order.driverId || "-"}</td>
                        <td style={st.td}>
                          {order.paymentType ? <span style={{ ...st.badge, backgroundColor: "#f3f4f6", color: "#374151" }}>{order.paymentType}</span> : "-"}
                          {order.cashAmount > 0 && <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>AED {order.cashAmount}</div>}
                        </td>
                        <td style={st.td}>{order.orderDate || "-"}</td>
                        <td style={st.td}>
                          <button style={st.btnSmall} onClick={(e) => { e.stopPropagation(); fetchOrderDetail(order.jobId); }}>
                            <i className="fa fa-eye" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ ORDERS TAB ============ */}
      {activeTab === "orders" && (
        <div>
          <div style={st.card}>
            <div style={st.cardHeader}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={st.cardTitle}>
                  <i className="fa fa-table" style={{ marginRight: "8px" }} />
                  All Orders
                  <span style={st.countBadge}>{totalElements}</span>
                </h3>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} style={{ ...st.select, width: "auto" }}>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              {loading ? (
                <div style={st.spinner}><div style={st.spinnerInner} /></div>
              ) : orders.length === 0 ? (
                <div style={st.emptyState}>
                  <i className="fa fa-inbox" style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "12px" }} />
                  <div style={{ fontSize: "15px", color: "#6b7280" }}>No orders found</div>
                  <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "4px" }}>Use the search bar or advanced filters to find orders</div>
                </div>
              ) : (
                <table style={st.table}>
                  <thead>
                    <tr style={st.tableHead}>
                      <th style={st.th}>Job ID</th>
                      <th style={st.th}>Status</th>
                      <th style={st.th}>Type</th>
                      <th style={st.th}>Receiver</th>
                      <th style={st.th}>Contact</th>
                      <th style={st.th}>Driver</th>
                      <th style={st.th}>Payment</th>
                      <th style={st.th}>Order Date</th>
                      <th style={st.th}>Address</th>
                      <th style={st.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => (
                      <tr key={idx} style={st.tr}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f0f7ff"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                          onClick={() => fetchOrderDetail(order.jobId)}>
                        <td style={st.td}>
                          <span style={st.mono}>{order.jobId}</span>
                          {order.businessRefNo && <div style={{ fontSize: "11px", color: "#9ca3af" }}>{order.businessRefNo}</div>}
                        </td>
                        <td style={st.td}>{statusBadge(order.status)}</td>
                        <td style={st.td}>{order.type || "-"}</td>
                        <td style={st.td}>{order.receiverName || "-"}</td>
                        <td style={st.td}>
                          <div style={{ fontSize: "13px" }}>{order.contactName || "-"}</div>
                          {order.contactNumber && <div style={{ fontSize: "11px", color: "#9ca3af" }}>{order.contactNumber}</div>}
                        </td>
                        <td style={st.td}>{order.driverId || "-"}</td>
                        <td style={st.td}>
                          {order.paymentType ? <span style={{ ...st.badge, backgroundColor: "#f3f4f6", color: "#374151" }}>{order.paymentType}</span> : "-"}
                          {order.cashAmount > 0 && <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>AED {order.cashAmount}</div>}
                        </td>
                        <td style={st.td}>{order.orderDate || "-"}</td>
                        <td style={{ ...st.td, maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={order.formattedAddress}>
                          {order.formattedAddress || "-"}
                        </td>
                        <td style={st.td}>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button style={st.btnSmall} onClick={(e) => { e.stopPropagation(); fetchOrderDetail(order.jobId); }} title="View Details">
                              <i className="fa fa-eye" />
                            </button>
                            <button style={st.btnSmallCopy} onClick={(e) => { e.stopPropagation(); copyToClipboard(order.jobId); }} title="Copy Job ID">
                              <i className="fa fa-copy" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={st.pagination}>
                <span style={st.pageInfo}>Page {page + 1} of {totalPages} ({totalElements} total)</span>
                <div style={{ display: "flex", gap: "4px" }}>
                  <button style={st.pageBtn} onClick={() => setPage(0)} disabled={page === 0}><i className="fa fa-angle-double-left" /></button>
                  <button style={st.pageBtn} onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}><i className="fa fa-angle-left" /></button>
                  <button style={st.pageBtn} onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}><i className="fa fa-angle-right" /></button>
                  <button style={st.pageBtn} onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}><i className="fa fa-angle-double-right" /></button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ ADVANCED SEARCH TAB ============ */}
      {activeTab === "search" && (
        <div>
          <div style={st.card}>
            <div style={st.cardHeader}><h3 style={st.cardTitle}><i className="fa fa-filter" style={{ marginRight: "8px" }} />Advanced Filters</h3></div>
            <div style={st.cardBody}>
              <div style={st.filterGrid}>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Job ID</label>
                  <input type="text" value={filterJobId} onChange={(e) => setFilterJobId(e.target.value)} placeholder="Enter Job ID" style={st.input} />
                </div>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>AWB Number</label>
                  <input type="text" value={filterAwb} onChange={(e) => setFilterAwb(e.target.value)} placeholder="Enter AWB" style={st.input} />
                </div>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Status</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={st.select}>
                    <option value="">All Statuses</option>
                    <option value="DELIVERED">DELIVERED</option>
                    <option value="PICKED_UP">PICKED UP</option>
                    <option value="ASSIGNED">ASSIGNED</option>
                    <option value="READY">READY</option>
                    <option value="FAILED">FAILED</option>
                    <option value="PENDING">PENDING</option>
                    <option value="CANCELLED">CANCELLED</option>
                    <option value="PLANNED">PLANNED</option>
                    <option value="UNASSIGNED">UNASSIGNED</option>
                    <option value="INSCAN">INSCAN</option>
                    <option value="DROP_ENROUTE">DROP ENROUTE</option>
                    <option value="PICKUP_ENROUTE">PICKUP ENROUTE</option>
                  </select>
                </div>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Driver ID</label>
                  <input type="text" value={filterDriverId} onChange={(e) => setFilterDriverId(e.target.value)} placeholder="Driver ID" style={st.input} />
                </div>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Date From</label>
                  <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={st.input} />
                </div>
                <div style={st.filterGroup}>
                  <label style={st.filterLabel}>Date To</label>
                  <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={st.input} />
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                <button style={st.btnPrimary} onClick={() => { setPage(0); searchOrders(); }} disabled={loading}>
                  <i className="fa fa-search" /> Search
                </button>
                <button style={st.btnSecondary} onClick={clearFilters}>
                  <i className="fa fa-eraser" /> Clear All
                </button>
              </div>
            </div>
          </div>

          {/* Search Results */}
          {orders.length > 0 && (
            <div style={st.card}>
              <div style={st.cardHeader}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 style={st.cardTitle}>Search Results <span style={st.countBadge}>{totalElements}</span></h3>
                  <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} style={{ ...st.select, width: "auto" }}>
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                  </select>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={st.table}>
                  <thead>
                    <tr style={st.tableHead}>
                      <th style={st.th}>Job ID</th>
                      <th style={st.th}>Status</th>
                      <th style={st.th}>Receiver</th>
                      <th style={st.th}>Driver</th>
                      <th style={st.th}>Date</th>
                      <th style={st.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, idx) => (
                      <tr key={idx} style={st.tr}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f0f7ff"}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                          onClick={() => fetchOrderDetail(order.jobId)}>
                        <td style={st.td}><span style={st.mono}>{order.jobId}</span></td>
                        <td style={st.td}>{statusBadge(order.status)}</td>
                        <td style={st.td}>{order.receiverName || order.contactName || "-"}</td>
                        <td style={st.td}>{order.driverId || "-"}</td>
                        <td style={st.td}>{order.orderDate || "-"}</td>
                        <td style={st.td}>
                          <button style={st.btnSmall} onClick={(e) => { e.stopPropagation(); fetchOrderDetail(order.jobId); }}>
                            <i className="fa fa-eye" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={st.pagination}>
                  <span style={st.pageInfo}>Page {page + 1} of {totalPages}</span>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button style={st.pageBtn} onClick={() => setPage(0)} disabled={page === 0}><i className="fa fa-angle-double-left" /></button>
                    <button style={st.pageBtn} onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}><i className="fa fa-angle-left" /></button>
                    <button style={st.pageBtn} onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}><i className="fa fa-angle-right" /></button>
                    <button style={st.pageBtn} onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}><i className="fa fa-angle-double-right" /></button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============ DETAIL MODAL ============ */}
      {showDetail && detail && (
        <div style={st.modalOverlay} onClick={() => setShowDetail(false)}>
          <div style={st.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <div>
                <h3 style={st.modalTitle}>
                  <i className="fa fa-file-text-o" style={{ marginRight: "8px" }} />
                  Order: {detail.order?.jobId}
                </h3>
                <div style={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
                  {statusBadge(detail.order?.status)}
                  {detail.order?.type && <span style={{ ...st.badge, backgroundColor: "#f3f4f6", color: "#374151" }}>{detail.order.type}</span>}
                  {detail.order?.paymentType && <span style={{ ...st.badge, backgroundColor: "#eff6ff", color: "#1e40af" }}>{detail.order.paymentType}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                <button style={st.btnSmallCopy} onClick={() => copyToClipboard(detail.order?.jobId)} title="Copy Job ID">
                  <i className="fa fa-copy" /> Copy ID
                </button>
                <button style={st.modalClose} onClick={() => setShowDetail(false)}>&times;</button>
              </div>
            </div>

            {/* Detail Tabs */}
            <div style={st.detailTabs}>
              {[
                { key: "info", icon: "fa-info-circle", label: "Order Info" },
                { key: "tracking", icon: "fa-history", label: `History (${detail.statusHistory?.length || 0})` },
                { key: "pod", icon: "fa-camera", label: `POD (${detail.uploads?.length || 0})` },
                { key: "podlogs", icon: "fa-cloud-upload", label: `POD Logs${podAuditCount ? ` (${podAuditCount})` : ""}` },
                { key: "scans", icon: "fa-exchange", label: `Scans (${detail.scanLogs?.length || 0})` },
                ...(detail.childAwbs?.length ? [{ key: "children", icon: "fa-sitemap", label: `Child AWBs (${detail.childAwbs.length})` }] : []),
                { key: "billing", icon: "fa-money", label: "Billing" },
              ].map((t) => (
                <button
                  key={t.key}
                  style={{ ...st.detailTab, ...(detailTab === t.key ? st.detailTabActive : {}) }}
                  onClick={() => setDetailTab(t.key)}
                >
                  <i className={`fa ${t.icon}`} style={{ marginRight: "4px" }} /> {t.label}
                </button>
              ))}
            </div>

            <div style={st.modalBody}>
              {/* ORDER INFO */}
              {detailTab === "info" && detail.order && (
                <div>
                  {/* Order Section */}
                  <div style={st.sectionTitle}><i className="fa fa-cube" style={{ marginRight: "6px" }} /> Order Information</div>
                  <div style={st.detailGrid}>
                    <DRow label="Job ID" value={detail.order.jobId} mono />
                    <DRow label="Business Ref" value={detail.order.businessRefNo} mono />
                    <DRow label="Type" value={detail.order.type} />
                    <DRow label="Status" value={detail.order.status} badge />
                    <DRow label="Driver ID" value={detail.order.driverId} />
                    <DRow label="Warehouse" value={detail.order.warehouse} />
                    <DRow label="Team ID" value={detail.order.teamId} />
                    <DRow label="Homebase" value={detail.order.homebaseId} />
                    <DRow label="Order Date" value={detail.order.orderDate} />
                    <DRow label="Expected Delivery" value={detail.order.expDlvDt} />
                    <DRow label="Scan DateTime" value={formatDate(detail.order.scanDateTime)} />
                    <DRow label="Assigned Date" value={detail.order.jobAssignedDate} />
                    <DRow label="Payment Type" value={detail.order.paymentType} />
                    <DRow label="COD Amount" value={detail.order.amountTransaction} />
                    <DRow label="Cash Collected" value={detail.order.cashAmount > 0 ? `AED ${detail.order.cashAmount}` : "-"} />
                    <DRow label="Shipment Type" value={detail.order.shipmentType} />
                    {detail.order.masterAwbNumber && <DRow label="Master AWB" value={detail.order.masterAwbNumber} mono />}
                    {detail.order.consolidatedWeight != null && <DRow label="Consolidated Weight" value={`${detail.order.consolidatedWeight} kg`} />}
                  </div>
                  {detail.order.itemDesc && (
                    <div style={{ marginTop: "12px" }}>
                      <DRow label="Item Description" value={detail.order.itemDesc} full />
                    </div>
                  )}

                  {/* Consignee */}
                  <div style={{ ...st.sectionTitle, marginTop: "24px" }}><i className="fa fa-user" style={{ marginRight: "6px" }} /> Consignee / Receiver</div>
                  <div style={st.detailGrid}>
                    <DRow label="Contact Name" value={detail.order.contactName} />
                    <DRow label="Contact Number" value={detail.order.contactNumber} />
                    <DRow label="Receiver Name" value={detail.order.receiverName} />
                    <DRow label="Handover Name" value={detail.order.handoverName} />
                    <DRow label="City" value={detail.order.city} />
                    <DRow label="Delivery Reason" value={detail.order.deliveryStatusReason} />
                  </div>
                  {detail.order.formattedAddress && (
                    <div style={{ marginTop: "8px" }}>
                      <DRow label="Full Address" value={detail.order.formattedAddress} full />
                    </div>
                  )}
                  {detail.order.reasonForReturn && (
                    <div style={{ marginTop: "8px", padding: "10px", backgroundColor: "#fef2f2", borderRadius: "8px", border: "1px solid #fecaca" }}>
                      <DRow label="Return Reason" value={detail.order.reasonForReturn} full />
                    </div>
                  )}

                  {/* Shipper */}
                  {detail.shipperInfo && (
                    <>
                      <div style={{ ...st.sectionTitle, marginTop: "24px" }}><i className="fa fa-building" style={{ marginRight: "6px" }} /> Shipper / Pickup Info</div>
                      <div style={st.detailGrid}>
                        <DRow label="Shipper Name" value={detail.shipperInfo.contactName} />
                        <DRow label="Shipper Number" value={detail.shipperInfo.contactNumber} />
                      </div>
                      {detail.shipperInfo.formattedAddress && (
                        <div style={{ marginTop: "8px" }}>
                          <DRow label="Shipper Address" value={detail.shipperInfo.formattedAddress} full />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* TRACKING / STATUS HISTORY */}
              {detailTab === "tracking" && (
                <div>
                  {!detail.statusHistory || detail.statusHistory.length === 0 ? (
                    <div style={st.emptyState}>
                      <i className="fa fa-history" style={{ fontSize: "40px", color: "#d1d5db", marginBottom: "12px" }} />
                      <div style={{ color: "#6b7280" }}>No status history available</div>
                    </div>
                  ) : (
                    <div style={st.timeline}>
                      {detail.statusHistory.map((h, i) => (
                        <div key={i} style={st.timelineItem}>
                          <div style={{ ...st.timelineDot, backgroundColor: i === 0 ? "#4f7df3" : "#d1d5db" }} />
                          <div style={{ ...st.timelineContent, border: i === 0 ? "1px solid #bfdbfe" : "1px solid #e5e7eb", backgroundColor: i === 0 ? "#eff6ff" : "#f9fafb" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              {statusBadge(h.status)}
                              <span style={{ fontSize: "12px", color: "#6b7280" }}>{formatDate(h.createdAt || h.scanDateTime)}</span>
                            </div>
                            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}>
                              {h.warehouse && <span><i className="fa fa-building-o" style={{ marginRight: "4px" }} />{h.warehouse}</span>}
                              {h.updatedBy && <span style={{ marginLeft: "12px" }}><i className="fa fa-user" style={{ marginRight: "4px" }} />{h.updatedBy}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* CHILD AWBs — optional multi-shipment grouping (Master AWB → child AWBs) */}
              {detailTab === "children" && (
                <div>
                  {detail.scannedChildAwb && (
                    <div style={{ marginBottom: "10px", padding: "8px 10px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", fontSize: "12px", color: "#1e3a8a" }}>
                      <i className="fa fa-info-circle" style={{ marginRight: "6px" }} />
                      Showing the <strong>master AWB</strong> for scanned sub-shipment <strong style={{ fontFamily: "monospace" }}>{detail.scannedChildAwb}</strong>
                    </div>
                  )}
                  {(detail.masterAwbNumber || detail.consolidatedWeight != null) && (
                    <div style={{ marginBottom: "12px", fontSize: "13px", color: "#374151", display: "flex", gap: "20px", flexWrap: "wrap" }}>
                      {detail.masterAwbNumber && (
                        <span><i className="fa fa-sitemap" style={{ marginRight: "6px" }} />Master AWB: <strong style={{ fontFamily: "monospace" }}>{detail.masterAwbNumber}</strong></span>
                      )}
                      {detail.consolidatedWeight != null && (
                        <span><i className="fa fa-balance-scale" style={{ marginRight: "6px" }} />Consolidated weight: <strong>{detail.consolidatedWeight} kg</strong></span>
                      )}
                    </div>
                  )}
                  {detail.childAwbs && detail.childAwbs.length > 0 && (
                    <div style={{ marginBottom: "12px", padding: "10px 12px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 500 }}>
                        <input type="checkbox"
                          checked={selectedChildren.size === detail.childAwbs.length && detail.childAwbs.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedChildren(new Set(detail.childAwbs.map((c) => c.childAwb)));
                            else setSelectedChildren(new Set());
                          }} />
                        Select all
                      </label>
                      {selectedChildren.size > 0 && (
                        <>
                          <span style={{ fontSize: "12px", color: "#64748b" }}>({selectedChildren.size} selected)</span>
                          <button style={{ ...st.btnSmall, backgroundColor: "#dcfce7", color: "#166534", borderColor: "#86efac" }}
                            disabled={updatingChildren} onClick={() => updateChildAwbStatus("PICKED_UP")}>
                            <i className="fa fa-check" style={{ marginRight: "4px" }} /> Mark Picked ({selectedChildren.size})
                          </button>
                          <button style={{ ...st.btnSmall, backgroundColor: "#bbf7d0", color: "#065f46", borderColor: "#6ee7b7" }}
                            disabled={updatingChildren} onClick={() => updateChildAwbStatus("DELIVERED")}>
                            <i className="fa fa-check-double" style={{ marginRight: "4px" }} /> Mark Delivered ({selectedChildren.size})
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {!detail.childAwbs || detail.childAwbs.length === 0 ? (
                    <div style={st.emptyState}>
                      <i className="fa fa-sitemap" style={{ fontSize: "40px", color: "#d1d5db", marginBottom: "12px" }} />
                      <div style={{ color: "#6b7280" }}>No child AWBs for this order</div>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                        <thead>
                          <tr style={{ textAlign: "left", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                            <th style={{ padding: "8px", width: "36px" }} />
                            <th style={{ padding: "8px" }}>#</th>
                            <th style={{ padding: "8px" }}>Child AWB</th>
                            <th style={{ padding: "8px" }}>Title</th>
                            <th style={{ padding: "8px" }}>Piece Ref</th>
                            <th style={{ padding: "8px" }}>Description</th>
                            <th style={{ padding: "8px" }}>Weight</th>
                            <th style={{ padding: "8px" }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.childAwbs.map((c, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "8px", textAlign: "center" }}>
                                <input type="checkbox"
                                  checked={selectedChildren.has(c.childAwb)}
                                  onChange={(e) => {
                                    const next = new Set(selectedChildren);
                                    if (e.target.checked) next.add(c.childAwb); else next.delete(c.childAwb);
                                    setSelectedChildren(next);
                                  }} />
                              </td>
                              <td style={{ padding: "8px", color: "#9ca3af" }}>{i + 1}</td>
                              <td style={{ padding: "8px", fontFamily: "monospace" }}>{c.childAwb || "-"}</td>
                              <td style={{ padding: "8px" }}>{c.title || "-"}</td>
                              <td style={{ padding: "8px" }}>{c.pieceReference || "-"}</td>
                              <td style={{ padding: "8px" }}>{c.description || "-"}</td>
                              <td style={{ padding: "8px" }}>{c.weight != null ? c.weight : "-"}</td>
                              <td style={{ padding: "8px" }}>{c.status ? statusBadge(c.status) : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ borderTop: "2px solid #e5e7eb", fontWeight: 600 }}>
                            <td style={{ padding: "8px" }} colSpan={6}>Consolidated weight (total)</td>
                            <td style={{ padding: "8px" }}>{detail.consolidatedWeight != null ? `${detail.consolidatedWeight} kg` : "-"}</td>
                            <td style={{ padding: "8px" }} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* POD AUDIT LOGS — every S3 upload attempt for this AWB (success + failure) */}
              {detailTab === "podlogs" && (
                <div>
                  {podAuditLogs.length === 0 ? (
                    <div style={st.emptyState}>
                      <i className="fa fa-cloud" style={{ fontSize: "40px", color: "#d1d5db", marginBottom: "12px" }} />
                      <div style={{ color: "#6b7280" }}>No POD upload attempts recorded for this AWB yet.</div>
                      <div style={{ color: "#9ca3af", fontSize: "12px", marginTop: "6px" }}>
                        Every upload from the driver app, the TMS dashboard, and Emirates V2 is logged here.
                      </div>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={st.table}>
                        <thead>
                          <tr style={st.tableHead}>
                            <th style={st.th}>When</th>
                            <th style={st.th}>Source</th>
                            <th style={st.th}>File</th>
                            <th style={st.th}>Size</th>
                            <th style={st.th}>Region used</th>
                            <th style={st.th}>Attempt</th>
                            <th style={st.th}>Status</th>
                            <th style={st.th}>Error / URL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {podAuditLogs.map((u) => (
                            <tr key={u.id} style={st.tr}>
                              <td style={st.td}>{formatDate(u.createdAt)}</td>
                              <td style={st.td}>
                                <span style={{ ...st.badge,
                                  backgroundColor: u.source === "MOBILE_POD" || u.source === "EMIRATES_V2" ? "#dbeafe" : "#f1f5f9",
                                  color: u.source === "MOBILE_POD" || u.source === "EMIRATES_V2" ? "#1e40af" : "#475569" }}>
                                  {u.source || "—"}
                                </span>
                              </td>
                              <td style={{ ...st.td, fontSize: "12px", wordBreak: "break-all", maxWidth: "260px" }}>
                                {u.fileName || u.s3Key || "—"}
                              </td>
                              <td style={st.td}>
                                {u.fileSizeBytes
                                  ? (u.fileSizeBytes < 1024
                                      ? `${u.fileSizeBytes} B`
                                      : u.fileSizeBytes < 1024 * 1024
                                        ? `${(u.fileSizeBytes / 1024).toFixed(1)} KB`
                                        : `${(u.fileSizeBytes / (1024 * 1024)).toFixed(2)} MB`)
                                  : "—"}
                              </td>
                              <td style={st.td}>
                                <div>{u.regionAttempted || "—"}</div>
                                {u.regionsTried && u.regionsTried !== u.regionAttempted && (
                                  <div style={{ fontSize: "11px", color: "#9ca3af" }}>tried: {u.regionsTried}</div>
                                )}
                              </td>
                              <td style={st.td}>{u.attemptNumber}/{u.totalAttempts}</td>
                              <td style={st.td}>{statusBadge(u.status)}</td>
                              <td style={{ ...st.td, maxWidth: "320px" }}>
                                {u.status === "FAILED" ? (
                                  <span style={{ color: "#991b1b", fontSize: "12px" }}>
                                    {u.errorCode ? <strong>{u.errorCode}: </strong> : null}{u.errorMessage}
                                  </span>
                                ) : (
                                  u.presignedUrl && (
                                    <a href={u.presignedUrl} target="_blank" rel="noopener noreferrer" style={st.uploadLink}>
                                      <i className="fa fa-external-link" /> Open image
                                    </a>
                                  )
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* POD & UPLOADS */}
              {detailTab === "pod" && (
                <div>
                  {!detail.uploads || detail.uploads.length === 0 ? (
                    <div style={st.emptyState}>
                      <i className="fa fa-camera" style={{ fontSize: "40px", color: "#d1d5db", marginBottom: "12px" }} />
                      <div style={{ color: "#6b7280" }}>No proof of delivery uploads found</div>
                    </div>
                  ) : (
                    <div style={st.uploadsGrid}>
                      {detail.uploads.map((u, i) => (
                        <PodImageCard key={i} upload={u} onZoom={setViewingImage} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* SCAN LOGS */}
              {detailTab === "scans" && (
                <div>
                  {!detail.scanLogs || detail.scanLogs.length === 0 ? (
                    <div style={st.emptyState}>
                      <i className="fa fa-exchange" style={{ fontSize: "40px", color: "#d1d5db", marginBottom: "12px" }} />
                      <div style={{ color: "#6b7280" }}>No Emirates scan logs found</div>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={st.table}>
                        <thead>
                          <tr style={st.tableHead}>
                            <th style={st.th}>ID</th>
                            <th style={st.th}>Order Status</th>
                            <th style={st.th}>Sync Status</th>
                            <th style={st.th}>Scan Type</th>
                            <th style={st.th}>Retries</th>
                            <th style={st.th}>Error</th>
                            <th style={st.th}>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.scanLogs.map((sl, i) => (
                            <tr key={i} style={st.tr}>
                              <td style={st.td}>{sl.id}</td>
                              <td style={st.td}>{statusBadge(sl.orderStatus)}</td>
                              <td style={st.td}>{statusBadge(sl.status)}</td>
                              <td style={st.td}>{sl.scanType || "-"}</td>
                              <td style={st.td}>{sl.retryCount || 0}</td>
                              <td style={{ ...st.td, maxWidth: "200px" }}>
                                {sl.errorMessage ? (
                                  <span title={sl.errorMessage} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#991b1b", fontSize: "12px" }}>
                                    {sl.errorMessage}
                                  </span>
                                ) : "-"}
                              </td>
                              <td style={st.td}>{formatDate(sl.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* BILLING */}
              {detailTab === "billing" && detail.order && (
                <div>
                  <div style={st.sectionTitle}><i className="fa fa-money" style={{ marginRight: "6px" }} /> Billing & Settlement</div>
                  <div style={st.detailGrid}>
                    <DRow label="Billed Status" value={detail.order.billedStatus} badge />
                    <DRow label="Billed Date" value={detail.order.billedDate} />
                    <DRow label="CX Settled Status" value={detail.order.cxSettledStatus} badge />
                    <DRow label="CX Settled Date" value={detail.order.cxSettledDate} />
                    <DRow label="Payment Type" value={detail.order.paymentType} />
                    <DRow label="COD / Cash" value={detail.order.cashAmount > 0 ? `AED ${detail.order.cashAmount}` : "-"} />
                    <DRow label="Amount Transaction" value={detail.order.amountTransaction} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ IMAGE VIEWER MODAL ============ */}
      {viewingImage && (
        <div style={st.modalOverlay} onClick={() => setViewingImage(null)}>
          <div style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
            <button style={{ ...st.modalClose, position: "absolute", top: "-40px", right: "0", color: "#fff", fontSize: "28px" }} onClick={() => setViewingImage(null)}>&times;</button>
            <img src={viewingImage} alt="POD" style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: "12px", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }} />
          </div>
        </div>
      )}
    </div>
  );

  // ============ COMPONENTS ============

  function DRow({ label, value, badge, full, mono }) {
    return (
      <div style={full ? { ...st.dRow, gridColumn: "1 / -1" } : st.dRow}>
        <span style={st.dLabel}>{label}</span>
        <span style={{ ...st.dValue, ...(mono ? { fontFamily: "monospace", fontSize: "13px" } : {}) }}>
          {badge && value ? statusBadge(value) : (value || "-")}
        </span>
      </div>
    );
  }

  function PodImageCard({ upload: u, onZoom }) {
    // Route every image through the backend image-proxy. Two wins:
    //   1. The S3 buckets don't allow CORS GETs from the dashboard origin — fetch() of a
    //      presigned URL fails, breaking the client-side HEIC→JPEG conversion.
    //   2. When the backend isn't running on EC2 (local dev), presigning fails and
    //      documentPath comes back as a raw S3 key. The proxy streams bytes regardless.
    // `originalDocumentPath` is the canonical S3 key from the backend.
    const rawKey = u.originalDocumentPath || u.documentPath;
    const directSrc = typeof u.documentPath === "string" && /^https?:\/\//i.test(u.documentPath)
        ? u.documentPath : null;
    const proxySrc = rawKey ? `${BASE}/v1.0/images/proxy?key=${encodeURIComponent(rawKey)}` : null;
    const fileLooksHeic = typeof rawKey === "string" && /\.(heic|heif)(\?|$)/i.test(rawKey);
    const fileLooksImage = typeof rawKey === "string"
      && /\.(jpe?g|png|gif|webp|bmp|heic|heif|svg)(\?|$)/i.test(rawKey);

    const [convertedSrc, setConvertedSrc] = useState(null);
    const [converting, setConverting] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
      setConvertedSrc(null);
      if (!proxySrc || !fileLooksHeic) return;
      let cancelled = false;
      setConverting(true);
      (async () => {
        try {
          const heic2any = (await import("heic2any")).default;
          const resp = await fetch(proxySrc, { credentials: "omit" });
          if (!resp.ok) throw new Error(`proxy fetch failed: ${resp.status}`);
          const blob = await resp.blob();
          const jpegBlob = await heic2any({ blob, toType: "image/jpeg", quality: 0.85 });
          if (cancelled) return;
          setConvertedSrc(URL.createObjectURL(jpegBlob));
        } catch (err) {
          if (!cancelled) {
            console.warn("[PodImageCard] HEIC conversion failed for", rawKey, err);
            setFailed(true);
          }
        } finally {
          if (!cancelled) setConverting(false);
        }
      })();
      return () => { cancelled = true; };
    }, [proxySrc, fileLooksHeic, rawKey]);

    // Render preference: converted HEIC blob → backend proxy (works in all environments) → presigned URL
    const src = fileLooksHeic ? convertedSrc : (proxySrc || directSrc);
    const rawSrc = directSrc || proxySrc;
    const isImage = fileLooksImage;
    const isHeic = fileLooksHeic;

    return (
      <div style={st.uploadCard}>
        {converting ? (
          <div style={st.uploadPlaceholder}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: "28px", color: "#9ca3af" }} />
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "6px" }}>Converting HEIC…</div>
          </div>
        ) : src && !failed ? (
          <img
            src={src}
            alt={u.documentName || "POD"}
            style={st.uploadImg}
            onClick={() => onZoom(src)}
            onError={() => setFailed(true)}
          />
        ) : (
          <div style={st.uploadPlaceholder}>
            <i className={`fa ${isImage ? "fa-image" : "fa-file-o"}`} style={{ fontSize: "36px", color: "#9ca3af" }} />
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "6px" }}>
              {isHeic ? "HEIC preview unavailable — use Open full size" : "Image couldn't load"}
            </div>
          </div>
        )}
        <div style={st.uploadInfo}>
          <div style={{ fontWeight: 600, fontSize: "12px", color: "#374151" }}>
            {u.documentType || "Document"}
            {u.source && (
              <span style={{ marginLeft: "6px", padding: "1px 6px", borderRadius: "8px", fontSize: "10px",
                backgroundColor: u.source === "MOBILE" ? "#dbeafe" : "#f1f5f9",
                color: u.source === "MOBILE" ? "#1e40af" : "#475569" }}>
                {u.source === "MOBILE" ? "From driver app" : "From dashboard"}
              </span>
            )}
          </div>
          <div style={{ fontSize: "11px", color: "#9ca3af", wordBreak: "break-all", marginTop: "2px" }}>{u.documentName}</div>
          {u.uploadedAt && (
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
              <i className="fa fa-clock-o" style={{ marginRight: "4px" }} />
              {(() => { try { return new Date(u.uploadedAt).toLocaleString("en-GB"); } catch { return u.uploadedAt; } })()}
            </div>
          )}
          {(src || rawSrc) && (
            <a href={src || rawSrc} target="_blank" rel="noopener noreferrer" style={st.uploadLink}>
              <i className="fa fa-external-link" /> Open full size
            </a>
          )}
        </div>
      </div>
    );
  }
}

// ============ STYLES ============

const st = {
  container: { padding: "24px", backgroundColor: "#f5f7fa", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" },
  title: { fontSize: "24px", fontWeight: "700", color: "#1e293b", margin: 0 },
  subtitle: { fontSize: "14px", color: "#64748b", margin: "4px 0 0" },

  // Quick Search
  quickSearchWrap: { marginBottom: "20px" },
  quickSearchInner: { display: "flex", alignItems: "center", gap: "10px", backgroundColor: "#fff", borderRadius: "12px", padding: "8px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" },
  quickSearchInput: { flex: 1, border: "none", outline: "none", fontSize: "15px", padding: "8px 0", backgroundColor: "transparent", color: "#1e293b" },
  quickSearchClear: { background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "14px", padding: "4px" },
  quickSearchBtn: { padding: "8px 20px", backgroundColor: "#4f7df3", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "500" },

  // Tabs
  tabs: { display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" },
  tab: { padding: "10px 20px", fontSize: "14px", fontWeight: "500", border: "none", borderRadius: "8px", cursor: "pointer", transition: "all 0.2s" },
  tabActive: { backgroundColor: "#4f7df3", color: "#fff" },
  tabInactive: { backgroundColor: "#e2e8f0", color: "#475569" },

  // Cards
  card: { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "20px", border: "1px solid #e2e8f0" },
  cardHeader: { padding: "14px 20px", borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc", borderRadius: "12px 12px 0 0" },
  cardTitle: { fontSize: "15px", fontWeight: "600", color: "#334155", margin: 0, display: "inline" },
  cardBody: { padding: "20px" },

  // Stats
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "16px", marginBottom: "20px" },
  statCard: { backgroundColor: "#fff", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "16px" },
  statIcon: { fontSize: "28px" },
  statNumber: { fontSize: "28px", fontWeight: "700", color: "#1e293b" },
  statLabel: { fontSize: "13px", color: "#64748b", marginTop: "2px" },

  // Filters
  filterGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "12px" },
  filterGroup: { display: "flex", flexDirection: "column", gap: "4px" },
  filterLabel: { fontSize: "12px", fontWeight: "600", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" },
  input: { padding: "9px 12px", fontSize: "14px", border: "1px solid #d1d5db", borderRadius: "8px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" },
  select: { padding: "9px 12px", fontSize: "14px", border: "1px solid #d1d5db", borderRadius: "8px", outline: "none", backgroundColor: "#fff", boxSizing: "border-box" },

  // Buttons
  btnPrimary: { padding: "10px 20px", fontSize: "14px", fontWeight: "500", backgroundColor: "#4f7df3", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
  btnSecondary: { padding: "10px 20px", fontSize: "14px", fontWeight: "500", backgroundColor: "#e2e8f0", color: "#475569", border: "none", borderRadius: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
  btnRefresh: { padding: "10px 16px", fontSize: "14px", fontWeight: "500", backgroundColor: "#fff", color: "#475569", border: "1px solid #d1d5db", borderRadius: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
  btnSmall: { padding: "6px 10px", fontSize: "12px", backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" },
  btnSmallCopy: { padding: "6px 10px", fontSize: "12px", backgroundColor: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" },

  // Table
  table: { width: "100%", borderCollapse: "collapse" },
  tableHead: { backgroundColor: "#f8fafc" },
  th: { padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #e2e8f0" },
  tr: { borderBottom: "1px solid #f1f5f9", cursor: "pointer", transition: "background 0.15s" },
  td: { padding: "12px 16px", fontSize: "13px", color: "#334155", verticalAlign: "middle" },
  mono: { fontFamily: "monospace", fontSize: "12px", color: "#2563eb", fontWeight: "500" },

  // Badge
  badge: { padding: "3px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "600", display: "inline-block" },
  countBadge: { marginLeft: "8px", padding: "2px 10px", backgroundColor: "#e2e8f0", borderRadius: "12px", fontSize: "12px", fontWeight: "600", color: "#475569" },

  // Pagination
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderTop: "1px solid #e2e8f0" },
  pageBtn: { padding: "8px 12px", fontSize: "14px", backgroundColor: "#fff", color: "#475569", border: "1px solid #d1d5db", borderRadius: "6px", cursor: "pointer" },
  pageInfo: { fontSize: "13px", color: "#64748b" },

  // Alerts
  alertError: { padding: "12px 16px", backgroundColor: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: "8px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  alertSuccess: { padding: "12px 16px", backgroundColor: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", borderRadius: "8px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  closeBtn: { cursor: "pointer", fontSize: "18px", fontWeight: "bold", paddingLeft: "12px" },

  // Empty state
  emptyState: { padding: "60px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" },

  // Modal
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modalContent: { backgroundColor: "#fff", borderRadius: "16px", width: "92%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
  modalHeader: { padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "sticky", top: 0, backgroundColor: "#fff", borderRadius: "16px 16px 0 0", zIndex: 1 },
  modalTitle: { fontSize: "18px", fontWeight: "600", color: "#1e293b", margin: 0 },
  modalClose: { fontSize: "24px", background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: "0 4px" },
  modalBody: { padding: "24px" },

  // Detail tabs
  detailTabs: { display: "flex", gap: "0", borderBottom: "1px solid #e2e8f0", overflowX: "auto", position: "sticky", top: "72px", backgroundColor: "#fff", zIndex: 1 },
  detailTab: { padding: "12px 16px", fontSize: "13px", fontWeight: "500", border: "none", background: "transparent", cursor: "pointer", color: "#64748b", borderBottom: "2px solid transparent", whiteSpace: "nowrap", transition: "all 0.2s" },
  detailTabActive: { color: "#4f7df3", borderBottom: "2px solid #4f7df3", fontWeight: "600" },

  // Detail grid
  sectionTitle: { fontSize: "14px", fontWeight: "600", color: "#334155", marginBottom: "12px", paddingBottom: "8px", borderBottom: "1px solid #f1f5f9" },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" },
  dRow: { display: "flex", flexDirection: "column", padding: "6px 0" },
  dLabel: { fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" },
  dValue: { fontSize: "14px", color: "#1e293b", marginTop: "2px" },

  // Timeline
  timeline: { position: "relative", paddingLeft: "24px" },
  timelineItem: { position: "relative", paddingBottom: "16px", borderLeft: "2px solid #e5e7eb", paddingLeft: "20px", marginLeft: "6px" },
  timelineDot: { position: "absolute", left: "-7px", top: "4px", width: "12px", height: "12px", borderRadius: "50%", border: "2px solid #fff", boxShadow: "0 0 0 2px #e5e7eb" },
  timelineContent: { padding: "10px 14px", borderRadius: "8px" },

  // Uploads
  uploadsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "16px" },
  uploadCard: { border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", transition: "box-shadow 0.2s", backgroundColor: "#fff" },
  uploadImg: { width: "100%", height: "320px", objectFit: "contain", backgroundColor: "#f8fafc", cursor: "zoom-in", borderBottom: "1px solid #e2e8f0", display: "block" },
  uploadPlaceholder: { width: "100%", height: "320px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  uploadInfo: { padding: "12px" },
  uploadLink: { fontSize: "12px", color: "#2563eb", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "4px", marginTop: "6px" },

  // Spinner
  spinner: { display: "flex", justifyContent: "center", padding: "40px" },
  spinnerInner: { width: "36px", height: "36px", border: "3px solid #e2e8f0", borderTopColor: "#4f7df3", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};
